import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import {
  Modal,
  Button,
  Loader,
  Avatar,
  AvatarSize,
  TextField,
  Iconography,
  IconographySizes,
  IconographyThemes,
  LoaderSizes,
  TextArea,
  Dropdown,
} from "@hid-galaxy-ui/galaxy-react";
import axios from "axios";
import { UserListType } from "../../../../../redux/slices/user/Types";

import "./HIDUserFormModal.scss";

const HIDUserFormModal: React.FC<{
  user?: any;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: "Add" | "Update";
  onSuccess?: () => void;
}> = ({ user, setModalOpen, mode, onSuccess }) => {
  const [userDetails, setUserDetails] = React.useState<any>({
    title: "",
    description: "",
    category: "",
    firstName: "",
    lastName: "",
    email: "",
    role: "",
  });
  const [view, setView] = React.useState<"detail" | "status">("detail");
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const { status } = useAppSelector((state) => state.user);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const headerText =
    showSuccessModal || errorMessage
      ? ""
      : mode === "Add"
      ? "Add New Feature"
      : "Update Feature";

  React.useEffect(() => {
    if (user) {
      setUserDetails({
        title: user.title || "",
        description: user.description || "",
        category: user.category || "",
      });
    }
  }, [user]);

  const categoryOptions = [
    { label: "Product improvement", value: "product" },
    { label: "Process / Operations", value: "process" },
    { label: "Creative idea", value: "creative" },
    { label: "Research & Innovation", value: "research" },
  ];

  const addOrUpdateHIDUser = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Get logged-in user name from Redux (same as header)
      const loggedUserName = loggedUserDetail
        ? `${loggedUserDetail.first_name || ""} ${
            loggedUserDetail.last_name || ""
          }`.trim()
        : "";
      const loggedInUser = loggedUserName || "User";

      // Get partner ID from logged user ID
      const partnerID = loggedUserDetail?.ID?.toString() || "123";

      if (mode === "Add") {
        // Generate unique feature ID with timestamp and random component
        const timestamp = Date.now();
        const randomComponent = Math.random()
          .toString(36)
          .substring(2, 8)
          .toUpperCase();
        const featureID = `FRI-${timestamp}-${randomComponent}`;

        // Format date to MySQL datetime format (YYYY-MM-DD HH:mm:ss.S)
        const now = new Date();
        const formattedDate = `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
          now.getHours()
        ).padStart(2, "0")}:${String(now.getMinutes()).padStart(
          2,
          "0"
        )}:${String(now.getSeconds()).padStart(2, "0")}.${Math.floor(
          now.getMilliseconds() / 100
        )}`;

        console.log("🆔 Generated unique feature ID:", featureID);

        // Prepare API payload
        const apiPayload = {
          friID: featureID,
          description: userDetails.description,
          submittedby: loggedInUser,
          submittedDate: formattedDate,
          partnerID: partnerID,
          title: userDetails.title,
          category: userDetails.category,
        };

        console.log("📤 Creating new feature with payload:", apiPayload);

        // Call POST API
        const response = await axios.post(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-supported`,
          apiPayload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        setShowSuccessModal(true);

        // Also save to sessionStorage for local display
        const featureData = JSON.parse(
          sessionStorage.getItem("feature_details") || "[]"
        );
        featureData.push({
          id: featureID,
          friID: featureID,
          title: userDetails.title,
          description: userDetails.description,
          category: userDetails.category,
          createdBy: loggedInUser,
          date: formattedDate,
          comments: [],
        });
        sessionStorage.setItem("feature_details", JSON.stringify(featureData));

        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event("featuresUpdated"));

        if (onSuccess) onSuccess();
      } else {
        // Update mode
        console.log("📝 Updating feature:", user);

        // Prepare API payload for update
        const updatePayload = {
          friID: user.friID || user.id,
          description: userDetails.description,
          submittedby: user.submittedby || loggedInUser,
          submittedDate:
            user.submittedDate || user.date || new Date().toISOString(),
          partnerID: partnerID,
          title: userDetails.title,
          category: userDetails.category,
        };

        // Call PUT API - use friID in the URL
        const featureId = user.friID || user.id;
        const response = await axios.put(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-supported/${featureId}`,
          updatePayload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        setShowSuccessModal(true);

        // Update sessionStorage
        const featureData = JSON.parse(
          sessionStorage.getItem("feature_details") || "[]"
        );
        const updateIndex = featureData.findIndex(
          (data: any) => data.id === user.id
        );

        if (updateIndex !== -1) {
          featureData[updateIndex] = {
            ...featureData[updateIndex],
            title: userDetails.title,
            description: userDetails.description,
            category: userDetails.category,
          };
          sessionStorage.setItem(
            "feature_details",
            JSON.stringify(featureData)
          );
        }

        // Dispatch custom event
        window.dispatchEvent(new Event("featuresUpdated"));

        if (onSuccess) onSuccess();
      }
      setIsSubmitting(false);
    } catch (error: any) {
      console.error("Error saving feature:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save feature. Please try again.";
      setErrorMessage(errorMessage);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return userDetails.title.trim() && userDetails.category;
  };

  const renderView = (view: "detail" | "status") => {
    const statusBoolean =
      mode === "Add" ? status.addUserStatus : status.updateUserStatus;
    const statusMessage =
      mode === "Add" ? status.addUserMessage : "Something went wrong!";
    switch (view) {
      case "detail":
        return (
          <>
            <div className="details-form">
              {/* <Avatar size={AvatarSize.XLarge} isEditable={false} /> */}
              <div className="input-row-wrapper">
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">
                      Title<span>*</span>
                    </label>
                    <TextField
                      // label="Title"
                      id="title"
                      placeholder="Enter feature title"
                      name="title"
                      maxLength={100}
                      theme="dark"
                      value={userDetails.title}
                      onChange={(e: any) => {
                        setUserDetails({
                          ...userDetails,
                          title: e.target.value,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="input-row-wrapper">
                <div className="input-col-wrapper">
                  <div className="label-field-container">
                    <label className="field-label">
                      Category<span>*</span>
                    </label>
                    <Dropdown
                      // label="Category"
                      id="category"
                      name="category"
                      options={categoryOptions}
                      selected={categoryOptions.find(
                        (opt) => opt.value === userDetails.category
                      )}
                      onSelect={(option) =>
                        setUserDetails({
                          ...userDetails,
                          category: option?.value || "",
                        })
                      }
                      placeholder="Select category"
                      isClearable={false}
                    />
                  </div>
                </div>
              </div>
              <div className="input-row-wrapper">
                <div className="label-field-container">
                  <TextArea
                    label="Description"
                    id="description"
                    placeholder="Enter feature description"
                    name="description"
                    maxLength={1000}
                    theme="dark"
                    value={userDetails.description}
                    onChange={(e: any) => {
                      setUserDetails({
                        ...userDetails,
                        description: e.target.value,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
            {!showSuccessModal && !errorMessage && (
              <div className="button-group">
                <Button
                  variant="secondary"
                  label="Cancel"
                  onClick={() => setModalOpen(false)}
                  disabled={isSubmitting}
                />
                <Button
                  variant="primary"
                  label={
                    isSubmitting
                      ? mode === "Add"
                        ? "Creating..."
                        : "Updating..."
                      : mode === "Add"
                      ? "Add"
                      : "Update"
                  }
                  disabled={!isFormValid() || isSubmitting}
                  onClick={() => {
                    addOrUpdateHIDUser();
                  }}
                />
              </div>
            )}
          </>
        );
      default:
        return (
          <div className="status-container">
            {status.addUserLoading || status.updateUserLoading ? (
              <div className="loading">
                <Loader variant={LoaderSizes.large} />
              </div>
            ) : statusBoolean ? (
              <>
                <Iconography
                  icon="circleCheck"
                  size={IconographySizes.XLarge}
                  theme={IconographyThemes.Success}
                  className="status-icon"
                />
                <div className="message">
                  {mode === "Add"
                    ? "HID User Added with Invitation Sent"
                    : "HID User Updated"}
                </div>
                <div className="user-info" style={{ gap: "8px" }}>
                  <div className="name">{`${userDetails.firstName} ${userDetails.lastName}`}</div>
                  <div className="email">{userDetails.email}</div>
                  <div className="role">{userDetails.role}</div>
                </div>
                <Button
                  variant="primary"
                  label="Close"
                  onClick={() => setModalOpen(false)}
                />
              </>
            ) : (
              <>
                <Iconography
                  icon="circleXmark"
                  size={IconographySizes.XLarge}
                  theme={IconographyThemes.Error}
                  className="status-icon"
                />
                <div className="message">{statusMessage}</div>
                <Button
                  variant="primary"
                  label="Close"
                  onClick={() => setModalOpen(false)}
                />
              </>
            )}
          </div>
        );
    }
  };

  return (
    <Modal
      headerText={headerText}
      width={showSuccessModal || errorMessage ? 500 : 420}
      onClose={() => setModalOpen(false)}
      className="hid-user-form-modal"
    >
      <div
        className={`user-form-container ${
          showSuccessModal || errorMessage ? "center-content" : ""
        }`}
      >
        {showSuccessModal ? (
          <div className="status-message-container">
            <Iconography
              icon="circleCheck"
              size={IconographySizes.XLarge}
              theme={IconographyThemes.Success}
            />
            <div className="message">
              {mode === "Add"
                ? "Feature Created Successfully"
                : "Feature Updated Successfully"}
            </div>
          </div>
        ) : errorMessage ? (
          <div className="status-message-container">
            <Iconography
              icon="circleXmark"
              size={IconographySizes.XLarge}
              theme={IconographyThemes.Error}
            />
            <div className="message">{errorMessage}</div>
          </div>
        ) : (
          renderView(view)
        )}
      </div>
    </Modal>
  );
};

export default HIDUserFormModal;
